import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider } from '@/components/AuthProvider';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignupForm } from '@/components/auth/SignupForm';
import { ForgotPassword } from '@/components/auth/ForgotPassword';
import { ResetPassword } from '@/components/auth/ResetPassword';
import { MagicLinkLogin } from '@/components/auth/MagicLinkLogin';
import { RequestAccess } from '@/components/auth/RequestAccess';
import { CheckoutSuccess } from '@/components/auth/CheckoutSuccess';
import { Onboarding } from '@/components/onboarding/Onboarding';
import { TrialOnboarding } from '@/components/onboarding/TrialOnboarding';
import { WorkspaceSetup } from '@/components/workspace/WorkspaceSetup';
import { WorkspaceSelection } from '@/components/workspace/WorkspaceSelection';
import { WorkspaceSettings } from '@/components/workspace/WorkspaceSettings';
import { WorkspaceGuard } from '@/components/workspace/WorkspaceGuard';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { Dashboard } from '@/components/dashboard/Dashboard';
import { ProjectList } from '@/components/project/ProjectList';
import { CreateProject } from '@/components/project/CreateProject';
import { FirstProjectSetup } from '@/components/project/FirstProjectSetup';
import { ProjectDetails } from '@/components/project/ProjectDetails';
import { ProjectTasks } from '@/components/project/ProjectTasks';
import { SettingsPage } from '@/components/settings/SettingsPage';
import { DevTesting } from '@/components/dev/DevTesting';
import { EmailTemplatePreview } from '@/components/dev/EmailTemplatePreview';
import { SuperPanel } from '@/components/admin/SuperPanel';

import { DocumentList } from '@/components/documents/DocumentList';
import { MembersPage } from '@/components/workspace/MembersPage';

const isDev = import.meta.env.DEV;

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={
            <AuthLayout>
              <LoginForm />
            </AuthLayout>
          } />

          <Route path="/trial-onboarding" element={
            <ProtectedRoute>
              <TrialOnboarding />
            </ProtectedRoute>
          } />

          <Route path="/signup" element={
            <AuthLayout>
              <SignupForm />
            </AuthLayout>
          } />

          <Route path="/forgot-password" element={
            <AuthLayout>
              <ForgotPassword />
            </AuthLayout>
          } />

          <Route path="/reset-password" element={
            <AuthLayout>
              <ResetPassword />
            </AuthLayout>
          } />

          <Route path="/magic-link" element={
            <AuthLayout>
              <MagicLinkLogin />
            </AuthLayout>
          } />

          <Route path="/request-access" element={<RequestAccess />} />

          {/* Checkout success - must be after auth but before workspace guard */}
          <Route path="/checkout-success" element={
            <ProtectedRoute>
              <CheckoutSuccess />
            </ProtectedRoute>
          } />

          {/* Dev testing routes - only available in development */}
          {isDev && (
            <>
              <Route path="/dev/testing" element={<DevTesting />} />
              <Route path="/dev/email-templates" element={<EmailTemplatePreview />} />
            </>
          )}

          {/* Onboarding route - protected but before workspace */}
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
            
          } />

          {/* Protected routes */}
          <Route path="/workspace/create" element={
            <ProtectedRoute>
              <WorkspaceSetup />
            </ProtectedRoute>
          } />

          <Route path="/workspace/select" element={
            <ProtectedRoute>
              <WorkspaceSelection />
            </ProtectedRoute>
          } />

          <Route path="/workspace/settings" element={
            <ProtectedRoute>
              <WorkspaceGuard>
                <WorkspaceSettings />
              </WorkspaceGuard>
            </ProtectedRoute>
          } />

          {/* Super Admin Panel - accessible only to superadmins */}
          <Route path="/admin/super-panel" element={
            <ProtectedRoute>
              <SuperPanel />
            </ProtectedRoute>
          } />

          {/* First project setup - outside dashboard layout for full screen */}
          <Route path="/projects/first-project" element={
            <ProtectedRoute>
              <WorkspaceGuard>
                <FirstProjectSetup />
              </WorkspaceGuard>
            </ProtectedRoute>
          } />

          {/* Dashboard routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <WorkspaceGuard>
                <DashboardLayout />
              </WorkspaceGuard>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="home" element={<Dashboard />} />
            {/* Legacy /dashboard URL — kept as a redirect so existing
                bookmarks and in-flight magic-link emails still land users
                on the home page. Safe to remove once we're confident no
                external link still points at /dashboard. */}
            <Route path="dashboard" element={<Navigate to="/home" replace />} />
            <Route path="dashboard-demo" element={<Dashboard isDemo={true} />} />
            <Route path="documents" element={<DocumentList />} />
            <Route path="projects" element={<ProjectList />} />
            <Route path="projects/create" element={<CreateProject />} />
            <Route path="projects/:id" element={<ProjectDetails />} />
            <Route path="projects/:id/tasks" element={<ProjectTasks />} />
            <Route path="members" element={<MembersPage />} />
            <Route path="user/settings" element={<SettingsPage />} />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;